/**
 * #/planner — Rotation Planner: la pizarra de rotaciones.
 *
 * Tres piezas, todas ya existentes en el repo, juntas por primera vez:
 *
 *  1. **la lógica pura** (`lib/planner.ts`): sexteto inicial → 6 rotaciones,
 *     quién queda en cada zona y las 7 restricciones de solapamiento (regla
 *     7.4) de cada una, con nombre y camiseta;
 *  2. **el log** (`GET /events`): para precargar el sexteto titular de un
 *     partido real y ponerle nombres a las camisetas;
 *  3. **la estadística por rotación** (`buildRotationReport`, la misma de la
 *     pestaña Rotaciones): sideout/break de CADA rotación planificada, del
 *     partido elegido o acumulando la temporada entera del equipo.
 *
 * La pizarra funciona vacía (se tipean 6 camisetas y listo): el partido es un
 * atajo para no tipear y la condición para cruzar con datos. Sin permiso de
 * `events:read` (rol viewer) la página lo dice y sigue sirviendo como pizarra.
 */
import { useMemo, useState } from 'react';
import type { TeamSide } from '@scout/core';
import type { MessageKey } from '../i18n/index';
import { useT } from '../i18n/use-t';
import { Card, Empty, ErrorBox, Loading, Notice } from '../components/States';
import { errorMessage, useAsync } from '../hooks';
import type { ApiClient } from '../lib/api-client';
import { effPct, pct, signed } from '../lib/format';
import { normalizeLog, readMatchMeta, type NormalizedEvent } from '../lib/log';
import {
  COURT_ROWS,
  duplicatedNumbers,
  firstLineup,
  lineupComplete,
  planRotations,
  statsForRotation,
  sumRotationReports,
  type Lineup,
  type OverlapPair,
  type PlannedRotation,
} from '../lib/planner';
import { buildRotationReport, type TeamRotationReport } from '../lib/projections';
import { eventAccess } from './match/access';

/** `'12'` → 12 · `''`/basura → null (el planner tolera huecos). */
function parseNumber(raw: string): number | null {
  const n = Number(raw.trim());
  return raw.trim() !== '' && Number.isInteger(n) && n >= 0 ? n : null;
}

export function PlannerPage({ client }: { client: ApiClient }): JSX.Element {
  const t = useT();
  const [matchId, setMatchId] = useState('');
  const [side, setSide] = useState<TeamSide>('home');
  /** El sexteto como se tipea (texto): índice 0 = P1. */
  const [slots, setSlots] = useState<string[]>(['', '', '', '', '', '']);
  const [setterRaw, setSetterRaw] = useState('');
  const [season, setSeason] = useState(false);

  const matches = useAsync(() => client.listMatches(), [client]);
  const events = useAsync(
    () => (matchId === '' ? Promise.resolve(null) : client.getEvents(matchId)),
    [client, matchId],
  );

  const derived = useMemo(() => {
    const log: NormalizedEvent[] = normalizeLog(events.data?.events ?? []);
    const meta = readMatchMeta(log, matchId);
    return {
      log,
      meta,
      rotation: log.length > 0 ? buildRotationReport(log) : null,
    };
  }, [events.data, matchId]);

  const access = eventAccess(matchId === '' ? null : events.error);
  const team = derived.meta.teams[side];
  const teamName = team.name;

  /**
   * Temporada del EQUIPO elegido: se baja el log de cada partido del archivo,
   * se reconstruye su reporte de rotaciones y se suman los del equipo con el
   * mismo nombre. Secuencial a propósito (no es una pantalla de apuro) y sólo
   * cuando la casilla está activa: son N pedidos.
   */
  const seasonStats = useAsync(async (): Promise<TeamRotationReport | null> => {
    if (!season || matchId === '' || derived.log.length === 0) return null;
    const list = matches.data ?? (await client.listMatches());
    const reports: TeamRotationReport[] = [];
    for (const summary of list) {
      const page =
        summary.matchId === matchId
          ? { events: derived.log.map((entry) => entry.event) }
          : await client.getEvents(summary.matchId).catch(() => null);
      if (!page) continue;
      const log = summary.matchId === matchId ? derived.log : normalizeLog(page.events);
      const meta = readMatchMeta(log, summary.matchId);
      const report = buildRotationReport(log);
      if (meta.teams.home.name === teamName) reports.push(report.home);
      if (meta.teams.away.name === teamName) reports.push(report.away);
    }
    return sumRotationReports(reports);
  }, [client, season, matchId, side, teamName, derived.log]);

  const lineup: Lineup = useMemo(() => slots.map(parseNumber), [slots]);
  const setter = parseNumber(setterRaw);
  const duplicates = duplicatedNumbers(lineup);
  const complete = lineupComplete(lineup);
  const plan = useMemo(() => planRotations(lineup, setter), [lineup, setter]);

  /** Las estadísticas que se cruzan: temporada si está activa, si no el partido. */
  const statsTeam: TeamRotationReport | null =
    season && matchId !== ''
      ? (seasonStats.data ?? null)
      : matchId !== '' && derived.rotation
        ? derived.rotation[side]
        : null;

  const prefill = (): void => {
    const found = firstLineup(derived.log, side, team);
    if (!found) return;
    setSlots(found.lineup.map((n) => String(n)));
    setSetterRaw(found.setter === null ? '' : String(found.setter));
  };
  const canPrefill = firstLineup(derived.log, side, team) !== null;

  /** Camisetas tipeadas (para el selector de armador). */
  const entered = useMemo(
    () => [...new Set(lineup.filter((n): n is number => n !== null))].sort((a, b) => a - b),
    [lineup],
  );

  return (
    <div>
      <div className="page-head">
        <div>
          <h1>{t('planner.title')}</h1>
          <div className="hint">{t('planner.intro')}</div>
        </div>
      </div>

      <Card title={t('planner.sourceCard')}>
        <p className="hint">{t('planner.sourceIntro')}</p>
        <div className="row" style={{ gap: 12, flexWrap: 'wrap' }}>
          <label className="row" style={{ gap: 6 }}>
            <span className="hint">{t('planner.matchLabel')}</span>
            <select
              value={matchId}
              onChange={(e) => {
                setMatchId(e.target.value);
                setSeason(false);
              }}
              style={{ width: 'auto' }}
            >
              <option value="">{t('planner.noMatch')}</option>
              {(matches.data ?? []).map((match) => (
                <option key={match.matchId} value={match.matchId}>
                  {match.name || match.matchId}
                </option>
              ))}
            </select>
          </label>
          <label className="row" style={{ gap: 6 }}>
            <span className="hint">{t('planner.teamLabel')}</span>
            <select
              value={side}
              onChange={(e) => setSide(e.target.value as TeamSide)}
              style={{ width: 'auto' }}
            >
              <option value="home">{derived.meta.teams.home.name}</option>
              <option value="away">{derived.meta.teams.away.name}</option>
            </select>
          </label>
          <button type="button" className="small" disabled={!canPrefill} onClick={prefill}>
            {t('planner.prefill')}
          </button>
          <label className="row" style={{ gap: 6 }}>
            <input
              type="checkbox"
              checked={season}
              disabled={matchId === '' || derived.log.length === 0}
              onChange={(e) => setSeason(e.target.checked)}
            />
            <span className="hint">{t('planner.seasonToggle')}</span>
          </label>
        </div>
        {matches.status === 'error' ? (
          <ErrorBox message={errorMessage(matches.error)} onRetry={matches.reload} />
        ) : null}
        {access === 'forbidden' ? (
          <Notice message={t('planner.noEvents')} hint={t('planner.noEventsHint')} />
        ) : null}
        {access === 'error' ? (
          <ErrorBox message={errorMessage(events.error)} onRetry={events.reload} />
        ) : null}
        {season && seasonStats.status === 'loading' ? (
          <Loading what={t('planner.loadingSeason')} />
        ) : null}
        {season && seasonStats.status === 'ok' && seasonStats.data === null ? (
          <p className="hint">{t('planner.seasonEmpty')}</p>
        ) : null}
      </Card>

      <Card title={t('planner.lineupCard')}>
        <p className="hint">{t('planner.lineupIntro')}</p>
        <div className="row" style={{ gap: 12, flexWrap: 'wrap' }}>
          {slots.map((value, i) => (
            <label key={i} className="row" style={{ gap: 6 }}>
              <span className="hint">P{i + 1}</span>
              <input
                value={value}
                inputMode="numeric"
                style={{ width: 64 }}
                aria-label={t('planner.slotLabel', { position: i + 1 })}
                onChange={(e) =>
                  setSlots((prev) => prev.map((old, j) => (j === i ? e.target.value : old)))
                }
              />
            </label>
          ))}
          <label className="row" style={{ gap: 6 }}>
            <span className="hint">{t('planner.setterLabel')}</span>
            <select
              value={setterRaw}
              onChange={(e) => setSetterRaw(e.target.value)}
              style={{ width: 'auto' }}
            >
              <option value="">{t('planner.noSetter')}</option>
              {entered.map((n) => (
                <option key={n} value={String(n)}>
                  #{n}
                  {team.names.get(n) ? ` ${team.names.get(n)}` : ''}
                </option>
              ))}
            </select>
          </label>
        </div>
        {duplicates.length > 0 ? (
          <p className="hint neg">
            {t('planner.duplicates', { numbers: duplicates.map((n) => `#${n}`).join(', ') })}
          </p>
        ) : null}
        {!complete && duplicates.length === 0 ? (
          <p className="hint">{t('planner.incomplete')}</p>
        ) : null}
        {setter === null ? <p className="hint">{t('planner.noSetterHint')}</p> : null}
      </Card>

      {lineup.every((n) => n === null) ? (
        <Card>
          <Empty>{t('planner.empty')}</Empty>
        </Card>
      ) : (
        <div className="grid-2">
          {plan.map((rotation) => (
            <RotationCard
              key={rotation.index}
              rotation={rotation}
              names={team.names}
              stats={statsForRotation(statsTeam, rotation.setterPosition)}
              setter={setter}
            />
          ))}
        </div>
      )}

      {matchId !== '' && statsTeam !== null && !statsTeam.tracked ? (
        <p className="hint">{t('planner.untracked')}</p>
      ) : null}
    </div>
  );
}

/** Una rotación: la cancha, el cruce con la estadística y los solapamientos. */
function RotationCard({
  rotation,
  names,
  stats,
  setter,
}: {
  rotation: PlannedRotation;
  names: ReadonlyMap<number, string | undefined>;
  stats: ReturnType<typeof statsForRotation>;
  setter: number | null;
}): JSX.Element {
  const t = useT();
  const title =
    rotation.setterPosition === null
      ? t('planner.rotationTitle', { order: rotation.index + 1 })
      : t('planner.rotationTitleSetter', {
          order: rotation.index + 1,
          position: rotation.setterPosition,
        });

  return (
    <Card
      title={title}
      actions={
        stats !== null ? (
          <div className="row" style={{ gap: 6 }}>
            <span className="chip muted" title={t('planner.ralliesTitle')}>
              {t('planner.rallies', { count: stats.rallies })}
            </span>
            <span className="chip muted" title={t('planner.sideoutTitle')}>
              {t('planner.sideout', { pct: pct(stats.sideoutPct) })}
            </span>
            <span className="chip muted" title={t('planner.breakTitle')}>
              {t('planner.breakPoint', { pct: pct(stats.breakPct) })}
            </span>
            <span
              className={`chip ${stats.netPoints < 0 ? 'bad' : 'ok'}`}
              title={t('planner.netTitle')}
            >
              {signed(stats.netPoints)}
            </span>
          </div>
        ) : undefined
      }
    >
      <div className="table-wrap">
        <table>
          <tbody>
            {COURT_ROWS.map((row, i) => (
              <tr key={i}>
                {row.map((zone) => {
                  const player = rotation.formation[zone - 1] ?? null;
                  const isSetter = player !== null && player === setter;
                  return (
                    <td key={zone} style={{ minWidth: 90 }}>
                      <div className="hint">Z{zone}</div>
                      <div>
                        {player === null ? '·' : <b>#{player}</b>}
                        {isSetter ? (
                          <span className="chip muted" style={{ marginLeft: 6 }}>
                            {t('planner.setterChip')}
                          </span>
                        ) : null}
                      </div>
                      {player !== null && names.get(player) ? (
                        <div className="hint">{names.get(player)}</div>
                      ) : null}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <h3 style={{ marginTop: 12 }}>{t('planner.overlapsTitle')}</h3>
      <ul className="hint" style={{ paddingLeft: 18, marginTop: 6 }}>
        {rotation.overlaps.map((pair, i) => (
          <li key={i}>{overlapSentence(pair, t)}</li>
        ))}
      </ul>
    </Card>
  );
}

function playerLabelOf(player: number | null, t: (key: MessageKey) => string): string {
  return player === null ? t('planner.emptySlot') : `#${player}`;
}

/** La restricción 7.4 dicha como se dice en el entrenamiento. */
function overlapSentence(
  pair: OverlapPair,
  t: (key: MessageKey, vars?: Record<string, string | number>) => string,
): string {
  const a = playerLabelOf(pair.playerA, t);
  const b = playerLabelOf(pair.playerB, t);
  return pair.kind === 'frontBack'
    ? t('planner.overlapFrontBack', { back: b, backZone: pair.zoneB, front: a, frontZone: pair.zoneA })
    : t('planner.overlapLeftRight', { left: a, leftZone: pair.zoneA, right: b, rightZone: pair.zoneB });
}
